import {Button} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import isEqual from 'react-fast-compare';
import {debounce} from 'ts-debounce';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {useAutoUpdatePreview} from 'analysis-tool/data-frame-creation/hooks/useAutoUpdatePreview';
import ChartContent, {QueryingChartContent} from 'charts/chart-content/ChartContent';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import DataFrameTableWrapper from 'data-frames/wrapper/DataFrameTableWrapper';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ScratchFilePaneContent, {QueryingScratchFilePaneContent} from 'pipelines/files/ScratchFilePaneContent';
import {ScratchFileContent} from 'pipelines/files/ScratchFileTypes';
import {stepTypeRequiresManualPreview} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettings';
import {PreviewChart, PreviewOutput, PreviewStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {getAllOutputStepDataRawDatas, getOutputStepDataShape} from 'pipelines/StepDataUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  getMostRecentData,
  NOT_FETCHED_ASYNC_STATE,
  AsyncStatus,
  AsyncState,
} from 'ui-library/hooks/useAsyncState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

const PREVIEW_CARD_MARGIN = 12;

const previewGlobalState = makeUseGlobalClientState<AsyncState<PreviewOutput>>(NOT_FETCHED_ASYNC_STATE);

export interface PreviewStepDataListProps {
  draftStep: PipelineStep;
  originalStep: PipelineStep;
  clientStateId: string;
  isComplete: boolean;
  errors: Array<string>;
}

export interface PreviewStepDataListState {
  previewDataFrames: Array<DataFrame>;
  previewCharts: Array<PreviewChart>;
  previewFiles: Array<ScratchFileContent>;
  previewStatus: AsyncStatus;
  statusMessage: string | null;
}

export function NoDataMessage(): JSX.Element {
  return (
    <div className={css(previewStyles.previewCard)}>
      <InlineBanner bannerType={InlineBannerType.WARNING} text="No preview data available." />
    </div>
  );
}

function StepDataList({
  previewDataFrames,
  previewCharts,
  previewFiles,
  clientStateId,
}: {
  previewDataFrames: Array<DataFrame>;
  previewCharts: Array<PreviewChart>;
  previewFiles: Array<ScratchFileContent>;
  clientStateId: string;
}): JSX.Element {
  if (!previewDataFrames.length && !previewCharts.length && !previewFiles.length) {
    return <NoDataMessage />;
  }

  const hasMultipleCharts = previewCharts.length > 1;
  const hasMultipleTables = previewDataFrames.length > 1;
  const hasMultipleFiles = previewFiles.length > 1;

  return (
    <div className={css(previewStyles.container)}>
      {previewCharts.map((chart, index) => (
        <div key={index} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleCharts ? `Output chart ${index + 1}` : 'Output chart'}
          </div>
          <div className={css(previewStyles.content)}>
            <ChartContent chart={chart} />
          </div>
        </div>
      ))}
      {previewDataFrames.map((dataFrame, index) => (
        <div key={index} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleTables ? `Output table ${index + 1}` : 'Output table'}
          </div>
          {dataFrame.rows.length === 0 && (
            <div className={css(previewStyles.banner)}>
              <InlineBanner bannerType={InlineBannerType.WARNING} text="This table has no rows." />
            </div>
          )}
          <div className={css(previewStyles.content)}>
            <DataFrameTableWrapper
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              clientStateId={`${clientStateId}-${index}`}
              dataFrame={dataFrame}
              defaultTableMode={DataFrameTableMode.TEXT}
              renderIsExcludedColumn={false}
            />
          </div>
        </div>
      ))}
      {previewFiles.map((file, index) => (
        <div key={file.id} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleFiles ? `Output file ${index + 1}` : 'Output file'}
          </div>
          <ScratchFilePaneContent scratchFile={file} />
        </div>
      ))}
    </div>
  );
}

function OriginalStepOutputPreview({step}: {step: PipelineStep}): JSX.Element {
  const dataFrames = useMemo(() => getAllOutputStepDataRawDatas(step, PipelineStepDataKind.DATASET), [step]);
  const charts = useMemo(() => getAllOutputStepDataRawDatas(step, PipelineStepDataKind.CHART), [step]);
  const scratchFiles = useMemo(() => getAllOutputStepDataRawDatas(step, PipelineStepDataKind.FILE), [step]);

  const totalItems = dataFrames.length + charts.length + scratchFiles.length;

  if (totalItems === 0) {
    return (
      <div className={css(previewStyles.previewCard)}>
        <InlineBanner bannerType={InlineBannerType.INFO} text="This step has no outputs." />
      </div>
    );
  }

  const hasMultipleCharts = charts.length > 1;
  const hasMultipleTables = dataFrames.length > 1;
  const hasMultipleFiles = scratchFiles.length > 1;

  return (
    <div className={css(previewStyles.container)}>
      {charts.map((chart, index) => (
        <div key={chart.id} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleCharts ? `Output chart ${index + 1}` : 'Output chart'}
          </div>
          <div className={css(previewStyles.content)}>
            <QueryingChartContent chartId={chart.id} />
          </div>
        </div>
      ))}
      {dataFrames.map((dataFrame, index) => (
        <div key={dataFrame.id} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleTables ? `Output table ${index + 1}` : 'Output table'}
          </div>
          <div className={css(previewStyles.content)}>
            <PersistedDataFrameTableWrapper
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              clientStateId={`output-preview-${step.id}-${dataFrame.id}`}
              dataFrameId={dataFrame.id}
            />
          </div>
        </div>
      ))}
      {scratchFiles.map((file, index) => (
        <div key={file.id} className={css(previewStyles.previewCard)}>
          <div className={css(previewStyles.title)}>
            {hasMultipleFiles ? `Output file ${index + 1}` : 'Output file'}
          </div>
          <QueryingScratchFilePaneContent fileId={file.id} />
        </div>
      ))}
    </div>
  );
}

function useLivePreviewData({
  draftStep,
  clientStateId,
}: {
  draftStep: PipelineStep;
  clientStateId: string;
}): PreviewStepDataListState {
  const [previewState, setPreviewState] = previewGlobalState.useGlobalClientState(clientStateId);

  const livePreviewRequestData = draftStep.config
    ? {
        stepType: draftStep.stepType,
        stepConfig: draftStep.config,
        stepInput: Object.fromEntries(
          draftStep.inputStepDatas.map((inputStepData) => [
            inputStepData.name,
            inputStepData.rawDatas.map(({id}) => id),
          ])
        ),
      }
    : null;

  const previewOutput = getMostRecentData(previewState);

  const previewDataFrames: Array<DataFrame> = [];
  const previewCharts: Array<PreviewChart> = [];
  const previewFiles: Array<ScratchFileContent> = [];

  if (previewOutput) {
    Object.entries(previewOutput.outputStepDatas).forEach(([stepDataName, data]) => {
      const stepDataShape = getOutputStepDataShape(draftStep.stepType, draftStep.stepVersion, stepDataName);

      if (stepDataShape.kind === PipelineStepDataKind.DATASET) {
        previewDataFrames.push(...(data as Array<DataFrame>));
      } else if (stepDataShape.kind === PipelineStepDataKind.CHART) {
        previewCharts.push(...(data as Array<PreviewChart>));
      } else if (stepDataShape.kind === PipelineStepDataKind.FILE) {
        previewFiles.push(...(data as Array<ScratchFileContent>));
      }
    });
  }

  const debouncedGeneratePreview = useMemo(
    () =>
      debounce(
        (
          data: PreviewStepParams<PipelineStepType>,
          setAsyncState: (
            nextState: AsyncState<PreviewOutput<PipelineStepType>, [PreviewStepParams<PipelineStepType>]>
          ) => void
        ) => {
          AnalysisActions.runPreviewAnalysisStep(data, {status: AsyncStatus.NOT_FETCHED}, setAsyncState);
        },
        300
      ),
    []
  );

  useAutoUpdatePreview(
    livePreviewRequestData as PreviewStepParams<PipelineStepType> | null,
    debouncedGeneratePreview,
    setPreviewState
  );

  return {
    previewDataFrames,
    previewCharts,
    previewFiles,
    previewStatus: previewState.status,
    statusMessage: (previewState.status === AsyncStatus.ERROR && previewState.message) || null,
  };
}

function DraftStepDataPreview({
  draftStep,
  clientStateId,
}: {
  draftStep: PipelineStep;
  clientStateId: string;
}): JSX.Element {
  const {previewDataFrames, previewCharts, previewFiles, previewStatus, statusMessage} = useLivePreviewData({
    draftStep,
    clientStateId,
  });

  if (previewStatus === AsyncStatus.LOADING) {
    return <CenteredHorizontalThrobber />;
  }

  if (previewStatus === AsyncStatus.ERROR) {
    if (statusMessage) {
      const cannotPreviewPattern = /^Step type .+ cannot be previewed\.$/;
      if (cannotPreviewPattern.test(statusMessage)) {
        return (
          <div className={css(previewStyles.previewCard)}>
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="This step type cannot be previewed without saving."
            />
          </div>
        );
      }
    }
    return (
      <div className={css(previewStyles.previewCard)}>
        <InlineBanner bannerType={InlineBannerType.ERROR} text="A preview table could not be generated." />
      </div>
    );
  }

  return (
    <StepDataList
      previewDataFrames={previewDataFrames}
      previewCharts={previewCharts}
      previewFiles={previewFiles}
      clientStateId={clientStateId}
    />
  );
}

function ManualPreviewStepDataList({
  draftStep,
  clientStateId,
}: {
  draftStep: PipelineStep;
  clientStateId: string;
}): JSX.Element {
  const [previewState, setPreviewState] = previewGlobalState.useGlobalClientState(clientStateId);

  const generatePreview = useCallback((): void => {
    if (!draftStep.config) {
      return;
    }
    const requestData: PreviewStepParams<PipelineStepType> = {
      stepType: draftStep.stepType,
      stepConfig: draftStep.config,
      stepInput: Object.fromEntries(
        draftStep.inputStepDatas.map((inputStepData) => [
          inputStepData.name,
          inputStepData.rawDatas.map(({id}) => id),
        ])
      ),
    };
    AnalysisActions.runPreviewAnalysisStep(requestData, {status: AsyncStatus.NOT_FETCHED}, setPreviewState);
  }, [draftStep, setPreviewState]);

  const previewOutput = getMostRecentData(previewState);

  const {previewDataFrames, previewCharts, previewFiles} = useMemo((): {
    previewDataFrames: Array<DataFrame>;
    previewCharts: Array<PreviewChart>;
    previewFiles: Array<ScratchFileContent>;
  } => {
    const dataFrames: Array<DataFrame> = [];
    const charts: Array<PreviewChart> = [];
    const files: Array<ScratchFileContent> = [];
    if (previewOutput) {
      Object.entries(previewOutput.outputStepDatas).forEach(([stepDataName, data]) => {
        const stepDataShape = getOutputStepDataShape(draftStep.stepType, draftStep.stepVersion, stepDataName);
        if (stepDataShape.kind === PipelineStepDataKind.DATASET) {
          dataFrames.push(...(data as Array<DataFrame>));
        } else if (stepDataShape.kind === PipelineStepDataKind.CHART) {
          charts.push(...(data as Array<PreviewChart>));
        } else if (stepDataShape.kind === PipelineStepDataKind.FILE) {
          files.push(...(data as Array<ScratchFileContent>));
        }
      });
    }
    return {previewDataFrames: dataFrames, previewCharts: charts, previewFiles: files};
  }, [previewOutput, draftStep.stepType, draftStep.stepVersion]);

  if (previewState.status === AsyncStatus.LOADING) {
    return <CenteredHorizontalThrobber />;
  }

  if (previewState.status === AsyncStatus.ERROR) {
    return (
      <div className={css(previewStyles.container)}>
        <div className={css(previewStyles.previewCard)}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text="A preview could not be generated." />
        </div>
        <div className={css(previewStyles.generateButtonContainer)}>
          <Button variant="secondary" onClick={generatePreview}>
            Retry Preview
          </Button>
        </div>
      </div>
    );
  }

  if (previewState.status === AsyncStatus.SUCCESS) {
    return (
      <div className={css(previewStyles.container)}>
        <StepDataList
          previewDataFrames={previewDataFrames}
          previewCharts={previewCharts}
          previewFiles={previewFiles}
          clientStateId={clientStateId}
        />
        <div className={css(previewStyles.generateButtonContainer)}>
          <Button variant="secondary" onClick={generatePreview}>
            Regenerate Preview
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={css(previewStyles.container)}>
      <div className={css(previewStyles.previewCard)}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text="Click the button below to generate a preview. This may take a moment."
        />
      </div>
      <div className={css(previewStyles.generateButtonContainer)}>
        <Button variant="primary" onClick={generatePreview}>
          Generate Preview
        </Button>
      </div>
    </div>
  );
}

export default function PreviewStepDataList({
  draftStep,
  originalStep,
  clientStateId,
  isComplete,
  errors,
}: PreviewStepDataListProps): JSX.Element {
  if (!isComplete || errors.length > 0) {
    const message = !isComplete ? 'Step config is incomplete' : 'Step config is invalid';
    return (
      <div className={css(previewStyles.previewCard)}>
        <InlineBanner bannerType={InlineBannerType.WARNING} text={message} />
      </div>
    );
  }

  const shouldShowOriginalData = isEqual(draftStep, originalStep);

  if (shouldShowOriginalData) {
    return <OriginalStepOutputPreview step={originalStep} />;
  }

  if (stepTypeRequiresManualPreview(draftStep.stepType, draftStep.stepVersion)) {
    return <ManualPreviewStepDataList draftStep={draftStep} clientStateId={clientStateId} />;
  }

  return <DraftStepDataPreview draftStep={draftStep} clientStateId={clientStateId} />;
}

const previewStyles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
    paddingBottom: PREVIEW_CARD_MARGIN / 2,
    paddingTop: PREVIEW_CARD_MARGIN / 2,
  },
  previewCard: {
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    height: 300,
    marginBottom: PREVIEW_CARD_MARGIN / 2,
    marginLeft: PREVIEW_CARD_MARGIN,
    marginRight: PREVIEW_CARD_MARGIN,
    marginTop: PREVIEW_CARD_MARGIN / 2,
  },
  title: {
    marginBottom: 12,
    fontWeight: 'bold',
    fontSize: 14,
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  banner: {
    marginBottom: 8,
  },
  generateButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginLeft: PREVIEW_CARD_MARGIN,
    marginRight: PREVIEW_CARD_MARGIN,
    marginTop: PREVIEW_CARD_MARGIN / 2,
    marginBottom: PREVIEW_CARD_MARGIN / 2,
  },
});
